import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth";
import { setPassphrase, cancelSubscription } from "@/api";
import { ACCOUNT } from "@/constants/testIds";
import AsciiDivider from "@/components/AsciiDivider";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Account = () => {
  const { user, refresh, loading } = useAuth();
  const [passphrase, setPassphraseValue] = useState("");
  const [savingPp, setSavingPp] = useState(false);
  const [ppMsg, setPpMsg] = useState(null);
  const [ppErr, setPpErr] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  if (loading) {
    return (
      <div className="term-container">
        <p className="muted">$ initializing session<span className="cursor" /></p>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="term-container">
        <p style={{ color: "var(--ax-danger)" }}>Session unavailable.</p>
      </div>
    );
  }

  const savePassphrase = async (e) => {
    e.preventDefault();
    setPpMsg(null);
    setPpErr(null);
    if (passphrase.length < 8) {
      setPpErr("Passphrase must be at least 8 characters.");
      return;
    }
    setSavingPp(true);
    try {
      await setPassphrase(passphrase);
      setPpMsg("Passphrase stored. Write down your codename: " + user.codename);
      setPassphraseValue("");
      await refresh();
    } catch (e) {
      setPpErr(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Could not save passphrase."
      );
    } finally {
      setSavingPp(false);
    }
  };

  const onCancel = async () => {
    setCancelBusy(true);
    try {
      await cancelSubscription();
      await refresh();
    } catch (e) {
      /* ignore */
    } finally {
      setCancelBusy(false);
    }
  };

  const subActive = user.subscription_status === "active";

  return (
    <div className="term-container fade-in" data-testid={ACCOUNT.root}>
      <div className="kbd-label">// session info</div>
      <h1 style={{ margin: "0.2rem 0 1rem", color: "var(--ax-accent)" }}>
        ACCOUNT
      </h1>

      <div className="term-card">
        <div className="kbd-label">CODENAME</div>
        <div
          data-testid={ACCOUNT.codename}
          style={{
            color: "var(--ax-accent)",
            fontSize: "1.4rem",
            letterSpacing: "0.05em",
            marginTop: 4,
          }}
        >
          {user.codename}
        </div>
        <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>
          This is your only identifier. AXIOM stores no email, no phone, no IP
          beyond rate-limit windows.
        </div>
      </div>

      <div className="term-card">
        <div className="kbd-label">SUBSCRIPTION</div>
        <div
          data-testid={ACCOUNT.subscriptionStatus}
          style={{ marginTop: 8, fontSize: 14 }}
        >
          {subActive ? (
            <>
              <span className="pill active">ACTIVE</span>{" "}
              <span className="muted">
                · {user.subscription_plan?.toUpperCase()} · access ends{" "}
                <strong style={{ color: "var(--ax-accent)" }}>
                  {formatDate(user.subscription_expires_at)}
                </strong>
              </span>
              {user.cancel_at_period_end && (
                <div
                  className="pill warn"
                  style={{ marginTop: 8, display: "inline-block" }}
                >
                  CANCELED · access stays until{" "}
                  {formatDate(user.subscription_expires_at)}
                </div>
              )}
            </>
          ) : (
            <>
              <span className="pill">FREE</span>{" "}
              <span className="muted">
                · {user.views_used}/{user.free_view_limit} free dossier views used
              </span>
              <div style={{ marginTop: "0.6rem" }}>
                <Link to="/">
                  <button>BROWSE DOSSIERS →</button>
                </Link>
              </div>
            </>
          )}
        </div>

        {subActive && !user.cancel_at_period_end && (
          <div style={{ marginTop: "0.8rem" }}>
            <button
              className="danger"
              data-testid={ACCOUNT.cancelButton}
              onClick={onCancel}
              disabled={cancelBusy}
            >
              {cancelBusy ? "CANCELING…" : "CANCEL AT PERIOD END"}
            </button>
            <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>
              You keep access until {formatDate(user.subscription_expires_at)}. No partial refunds.
            </div>
          </div>
        )}
      </div>

      <AsciiDivider />

      <div className="term-card">
        <div className="kbd-label">PASSPHRASE RECOVERY</div>
        {user.has_passphrase ? (
          <div className="muted" style={{ marginTop: 6 }}>
            A passphrase is set. To restore this codename on another device, go
            to <Link to="/recover">/recover</Link>.
          </div>
        ) : (
          <>
            <div className="muted" style={{ marginTop: 6 }}>
              Set an optional passphrase to recover this codename on other
              devices. AXIOM stores a one-way hash only.
            </div>
            <form onSubmit={savePassphrase} style={{ marginTop: "0.8rem" }}>
              <input
                data-testid={ACCOUNT.passphraseInput}
                type="password"
                value={passphrase}
                onChange={(e) => setPassphraseValue(e.target.value)}
                placeholder="passphrase (min 8 chars)"
                minLength={8}
                autoComplete="new-password"
              />
              <div style={{ marginTop: "0.5rem" }}>
                <button
                  type="submit"
                  data-testid={ACCOUNT.passphraseSubmit}
                  disabled={savingPp || passphrase.length < 8}
                >
                  {savingPp ? "SAVING…" : "SET PASSPHRASE"}
                </button>
              </div>
            </form>
            {ppMsg && (
              <div style={{ color: "var(--ax-accent)", marginTop: 8, fontSize: 12 }}>
                {ppMsg}
              </div>
            )}
            {ppErr && (
              <div style={{ color: "var(--ax-danger)", marginTop: 8, fontSize: 12 }}>
                {ppErr}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Account;
